import { trackCartEvent } from '@helpers/trackCartActions';
import React, { useEffect, useState, useCallback } from 'react';

interface PanelProps {
  children: React.ReactNode;
  alignment?: 'right' | 'left';
  context?: string | null;
  overlay?: boolean;
  triggers: {
    openTriggerEl?: HTMLElement | null;
    closeTriggerEl?: HTMLElement | null;
    toggleEl?: HTMLElement | null;
  };
  defaultOpen?: boolean;
  additionalClasses?: string;
  items?: DataLayerItem[]
}

const Panel: React.FC<PanelProps> = ({
  children,
  alignment = 'right',
  context = null,
  overlay = false,
  triggers,
  defaultOpen = false,
  additionalClasses = '',
  items = [],
}) => {
  const { openTriggerEl, closeTriggerEl, toggleEl } = triggers;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [wasOpen, setWasOpen] = useState(defaultOpen);

  const handleOpenEvent = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleCloseEvent = (e: Event | React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
  };

  const handleToggleEvent = useCallback(() => setIsOpen(!isOpen), [isOpen]);

  useEffect(() => {
    if (openTriggerEl) openTriggerEl.addEventListener('click', handleOpenEvent);
    if (closeTriggerEl) closeTriggerEl.addEventListener('click', handleCloseEvent);
    if (toggleEl) toggleEl.addEventListener('click', handleToggleEvent);
    if (context) {
      document.addEventListener(`${context}:open`, handleOpenEvent);
      document.addEventListener(`${context}:close`, handleCloseEvent);
    }

    return () => {
      if (openTriggerEl) openTriggerEl.removeEventListener('click', handleOpenEvent);
      if (closeTriggerEl) closeTriggerEl.removeEventListener('click', handleCloseEvent);
      if (toggleEl) toggleEl.removeEventListener('click', handleToggleEvent);
      if (context) {
        document.removeEventListener(`${context}:open`, handleOpenEvent);
        document.removeEventListener(`${context}:close`, handleCloseEvent);
      }
    };
  }, [openTriggerEl, closeTriggerEl, toggleEl, context, handleToggleEvent]);

  useEffect(() => {
    if (isOpen && !wasOpen) {
      // Trigger 'view_cart' event only when transitioning from closed to open
      setWasOpen(true);

      if (items.length) {
        trackCartEvent('view_cart', { items });
      }
    } else if (!isOpen) {
      setWasOpen(false);
    }

    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      // Cleanup in case the component unmounts
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen, wasOpen, items]);

  const closedClass = alignment === 'right' ? 'translate-x-full' : '-translate-x-full';

  return (
    <>
      {overlay && isOpen && (
        <div onClick={handleCloseEvent} className="block w-full h-full fixed inset-0 z-30 bg-black backdrop-filter backdrop-blur-md backdrop-saturate-150 bg-opacity-60 cursor-pointer transition delay-500 duration-500 ease-in-out "></div>
      )}
      <div
        className={`block max-w-panel w-full h-full fixed top-0 bottom-0 transition duration-500 ease-in-out z-40 bg-white overflow-scroll md:overflow-hidden
          ${alignment === 'right' ? 'right-0' : 'left-0'}
          ${isOpen ? 'translate-x-0' : closedClass}
          ${additionalClasses}
        `}
      >
        {children}
      </div>
    </>
  );
};

// Usage example
/**
 * Example usage of the Panel component:
 * 
 * ```tsx
 * import React, { useRef } from 'react';
 * import Panel from './Panel';
 * 
 * const App: React.FC = () => {
 *   const openButtonRef = useRef<HTMLButtonElement>(null);
 *   const closeButtonRef = useRef<HTMLButtonElement>(null);
 *   const toggleButtonRef = useRef<HTMLButtonElement>(null);
 * 
 *   return (
 *     <div>
 *       <h1>Panel Example</h1>
 *       <button ref={openButtonRef}>Open Panel</button>
 *       <button ref={closeButtonRef}>Close Panel</button>
 *       <button ref={toggleButtonRef}>Toggle Panel</button>
 * 
 *       <Panel
 *         alignment="right"
 *         context="panel-context"
 *         overlay={true}
 *         triggers={{
 *           openTriggerEl: openButtonRef.current,
 *           closeTriggerEl: closeButtonRef.current,
 *           toggleEl: toggleButtonRef.current,
 *         }}
 *         defaultOpen={false}
 *         additionalClasses="p-4"
 *       >
 *         <h2>Panel Content</h2>
 *         <p>This is the content of the panel.</p>
 *       </Panel>
 *     </div>
 *   );
 * };
 * 
 * export default App;
 * ```
 */

export default Panel;